import { useEffect, useRef } from 'react';
import { authService } from '../services/auth';

/**
 * Hook to automatically logout user after period of inactivity.
 * HIPAA compliance: 18 minute inactivity timeout (logout between 18-19 min)
 *
 * @param {boolean} isAuthenticated - Whether user is currently authenticated
 */
const useInactivityLogout = (isAuthenticated) => {
  const lastActivityRef = useRef(Date.now());
  const checkIntervalRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) {
      // Clear interval if user is not authenticated
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      return;
    }

    // Update last activity timestamp on user interaction
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };

    // Add event listeners for user activity
    const events = ['mousemove', 'click', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Check every 60 seconds if user has been inactive for 18+ minutes
    const INACTIVITY_THRESHOLD = 18 * 60 * 1000; // 18 minutes in milliseconds
    const CHECK_INTERVAL = 60 * 1000; // Check every 60 seconds

    checkIntervalRef.current = setInterval(async () => {
      const idleTime = Date.now() - lastActivityRef.current;

      if (idleTime >= INACTIVITY_THRESHOLD) {
        // Clear interval before logout
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;

        // Call logout API for HIPAA audit logging
        try {
          await authService.logout();
        } catch (err) {
          // Ignore errors - we're logging out anyway
          console.error('Inactivity logout API call failed:', err);
        }

        // Clear local storage and redirect with session expired message
        localStorage.removeItem('auth_token');
        window.location.href = '/login?message=session_expired';
      }
    }, CHECK_INTERVAL);

    // Cleanup on unmount
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [isAuthenticated]);
};

export default useInactivityLogout;
